import React, { useState } from 'react';

import {
    Bar,
    BarChart,
    CartesianGrid,
    Legend,
    ReferenceArea,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import { LoadingDataReducerStateType } from '../../reducers/loadingData';
import { getChartColor } from '../../utils/color';
import {
    barChartTickFFormatter as barChartTickFormatter,
    getCenteredYAxisLabelProps,
    getChartStateLabel,
    getTickFormat,
} from '../../utils/monitoring';

interface Props {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any[];
    labels: string[];
    dataLoadingState: LoadingDataReducerStateType;
    yAxisLabel?: string;
    yAxisDomain?: [number | string, number | string];
}

export const MonitoringPageBarChart: React.FC<Props> = ({
    data,
    labels,
    dataLoadingState,
    yAxisLabel,
    yAxisDomain,
}) => {
    const [hiddenLabels, setHiddenLabels] = useState<string[]>([]);

    const showChartStatus = ['loading', 'error'].includes(dataLoadingState);

    const format = getTickFormat(data[0].from, data[data.length - 1].to);

    return (
        <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data}>
                <XAxis dataKey="label" tickFormatter={(tick) => barChartTickFormatter(tick, format)} minTickGap={30} />
                <YAxis label={yAxisLabel && getCenteredYAxisLabelProps(yAxisLabel)} domain={yAxisDomain} />
                <Legend
                    onClick={({ value, inactive }) => {
                        inactive
                            ? setHiddenLabels(hiddenLabels.filter((x) => x !== value))
                            : setHiddenLabels([...hiddenLabels, value]);
                    }}
                />
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <Tooltip isAnimationActive={false} />
                {labels.map((label, i) => {
                    return (
                        <Bar
                            key={i}
                            dataKey={label}
                            stroke={getChartColor(label)}
                            fill={getChartColor(label)}
                            stackId="stackId"
                            hide={hiddenLabels.includes(label)}
                            isAnimationActive={false}
                        />
                    );
                })}
                {showChartStatus && (
                    <ReferenceArea
                        y1={0}
                        strokeWidth={0}
                        fill="#ccc"
                        fillOpacity={0.2}
                        isFront
                        {...{ label: getChartStateLabel(dataLoadingState as 'loading' | 'error') }}
                    />
                )}
            </BarChart>
        </ResponsiveContainer>
    );
};
